import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../../styles/LandingPage.css';
import '../appointment-confirmation/appointment-confirmation-page.css';
import { checkInAppointmentByToken, getAppointmentCheckInStatus } from '../../../api/storefront/client';

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.3l2.3 2.3 4.7-5.1" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5l3 1.8" />
  </svg>
);

const XCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6" />
    <path d="M15 9l-6 6" />
  </svg>
);

const statusIcons = {
  already_checked_in: CheckCircleIcon,
  expired: ClockIcon,
  invalid: XCircleIcon,
  unavailable: AlertCircleIcon,
};

const stateLabels = {
  checkinable: {
    title: 'Check in for your appointment',
    message: 'Tap the button below when you arrive.',
  },
  already_checked_in: {
    title: 'You are checked in',
    message: 'You have already checked in for this appointment.',
  },
  expired: {
    title: 'Check-in closed',
    message: 'This check-in link has expired.',
  },
  invalid: {
    title: 'Link unavailable',
    message: 'This check-in link is no longer valid.',
  },
  unavailable: {
    title: 'Check-in unavailable',
    message: 'Check-in is not available for this appointment right now.',
  },
};

const StorefrontAppointmentCheckInPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [pageState, setPageState] = useState('checkinable');
  const [message, setMessage] = useState(stateLabels.checkinable.message);

  useEffect(() => {
    let isCancelled = false;

    const loadStatus = async () => {
      setLoading(true);
      try {
        const response = await getAppointmentCheckInStatus(token);
        if (isCancelled) return;

        const state = response?.state || 'invalid';
        setPageState(state);
        setMessage(response?.message || stateLabels[state]?.message || stateLabels.invalid.message);
      } catch (error) {
        if (isCancelled) return;
        setPageState('invalid');
        setMessage('This check-in link is no longer valid.');
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadStatus();
    return () => { isCancelled = true; };
  }, [token]);

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      const response = await checkInAppointmentByToken(token);
      const state = response?.state || 'already_checked_in';
      setPageState(state);
      setMessage(response?.message || 'You are checked in. See you soon!');
    } catch (error) {
      setPageState('invalid');
      setMessage('We could not check you in from this link.');
    } finally {
      setCheckingIn(false);
    }
  };

  const displayCopy = stateLabels[pageState] || stateLabels.invalid;
  const StatusIcon = statusIcons[pageState] || AlertCircleIcon;
  const shouldShowStatusIcon = pageState !== 'checkinable';

  return (
    <div className="landing-page">
      <div className="appointment-confirmation-page">
        <div className="appointment-confirmation-page__card">
          {loading ? (
            <>
              <div className="appointment-confirmation-page__spinner" aria-hidden="true" />
              <p className="appointment-confirmation-page__message">Loading check-in…</p>
            </>
          ) : (
            <>
              {shouldShowStatusIcon && (
                <div className={`appointment-confirmation-page__status-icon appointment-confirmation-page__status-icon--${pageState}`}>
                  <StatusIcon />
                </div>
              )}
              <h1 className="appointment-confirmation-page__title">{displayCopy.title}</h1>
              <p className="appointment-confirmation-page__message">{message}</p>
              {pageState === 'checkinable' && (
                <button
                  type="button"
                  className="appointment-confirmation-page__button"
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                >
                  {checkingIn ? 'Checking in…' : 'Check in'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorefrontAppointmentCheckInPage;
